import { supabase } from "@/integrations/supabase/client";
import type { Product } from "@/components/DesignResults";

export interface FeedPost {
  id: string;
  design_id: string;
  image_url: string;
  design_style: string | null;
  products: Product[];
  created_at: string;
  like_count: number;
  liked_by_me: boolean;
  is_mine: boolean;
}

export const FEED_PAGE_SIZE = 24;

/** A design can be shared only by its owner, once it has finished generating. */
export function canShareDesign(
  design: { user_id: string | null; status: string; ai_image_url: string | null } | null,
  currentUserId: string | null,
): boolean {
  if (!design || !currentUserId) return false;
  return design.user_id === currentUserId && design.status === "ready" && !!design.ai_image_url;
}

// RPCs are new; cast keeps us independent of generated type timing.
const rpc = supabase.rpc.bind(supabase) as unknown as (
  fn: string,
  args?: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message: string } | null }>;

export async function fetchFeed(before: string | null, likedOnly: boolean): Promise<FeedPost[]> {
  const { data, error } = await rpc("get_feed", {
    _before: before,
    _limit: FEED_PAGE_SIZE,
    _liked_only: likedOnly,
  });
  if (error) throw new Error(error.message);
  return ((data as FeedPost[]) ?? []).map((p) => ({
    ...p,
    like_count: Number(p.like_count) || 0,
    products: Array.isArray(p.products) ? p.products : [],
  }));
}

export async function shareDesign(designId: string): Promise<void> {
  const { error } = await rpc("set_design_feed_opt_out", { _design_id: designId, _opt_out: false });
  if (error) throw new Error(error.message);
}

export async function unshareDesign(designId: string): Promise<void> {
  const { error } = await rpc("set_design_feed_opt_out", { _design_id: designId, _opt_out: true });
  if (error) throw new Error(error.message);
}

export async function isDesignShared(designId: string): Promise<boolean> {
  const { data } = await supabase.from("feed_posts").select("id").eq("design_id", designId).maybeSingle();
  return !!data;
}

export async function setLike(postId: string, userId: string, liked: boolean): Promise<void> {
  const q = liked
    ? supabase.from("feed_likes").insert({ post_id: postId, user_id: userId })
    : supabase.from("feed_likes").delete().eq("post_id", postId).eq("user_id", userId);
  const { error } = await q;
  if (error) throw new Error(error.message);
}

export async function setPostHidden(postId: string, hidden: boolean): Promise<void> {
  const { error } = await rpc("set_feed_post_hidden", { _post_id: postId, _hidden: hidden });
  if (error) throw new Error(error.message);
}
